import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { track } from '../../utils/analytics';
import HeroSection from '../../components/landing/HeroSection';
import EcoBadgesGrid from '../../components/landing/EcoBadgesGrid';
import RefillBadge from '../../components/landing/RefillBadge';
import TestimonialsCarousel from '../../components/landing/TestimonialsCarousel';
import FAQAccordion from '../../components/landing/FAQAccordion';
import ProductVariantSelector from '../../components/landing/ProductVariantSelector';
import BuyStickyButton from '../../components/landing/BuyStickyButton';
import OneClickCheckoutModal from '../../components/landing/OneClickCheckoutModal';
import ImageGallery from '../../components/landing/ImageGallery';
import VideoSection from '../../components/landing/VideoSection';
import HtmlBlock from '../../components/landing/HtmlBlock';
import { Eyebrow, SectionDivider } from '../../components/landing/LeafMark';

const fmtCOP = v => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);

function renderSeccion(s, i) {
  if (s.type === 'eco_badges_bar') return <EcoBadgesGrid key={i} titulo={s.title} items={s.items} />;
  if (s.type === 'ingredients_and_benefits') return (
    <div key={i} style={{ maxWidth: 640, margin: '0 auto', padding: '32px 20px' }}>
      <Eyebrow>{s.title}</Eyebrow>
      <ul style={{ paddingLeft: 20, color: 'var(--ink-light)', fontSize: 14, lineHeight: 2, marginTop: 14 }}>
        {s.items?.map((it, j) => <li key={j}>{it}</li>)}
      </ul>
    </div>
  );
  if (s.type === 'refill_info') return <RefillBadge key={i} titulo={s.title} description={s.description} />;
  if (s.type === 'testimonials') return <TestimonialsCarousel key={i} titulo={s.title} items={s.items} />;
  if (s.type === 'faq') return <FAQAccordion key={i} titulo={s.title} items={s.items} />;
  if (s.type === 'gallery') return <ImageGallery key={i} items={s.items} />;
  if (s.type === 'video') return <VideoSection key={i} url={s.url} caption={s.title} />;
  if (s.type === 'html_block') return <HtmlBlock key={i} html={s.html} />;
  return null;
}

export default function Landing() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [landing, setLanding] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [cantidad, setCantidad] = useState(1);
  const [variacionesSel, setVariacionesSel] = useState([]);
  const [adicionalVariante, setAdicionalVariante] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    api.get(`/landings/${slug}`)
      .then(r => {
        setLanding(r.data);
        track('view_landing', { pagina: `/landing/${slug}`, producto_id: r.data.producto.id, meta: { landing_slug: slug } });
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 40 }}>
      <div style={{ fontSize: 56 }}>🌿</div>
      <h2 style={{ fontFamily: 'var(--font-display)', color: 'var(--forest)' }}>Esta oferta ya no está disponible</h2>
      <button className="btn btn-primary" onClick={() => navigate('/')}>Ver todos los productos</button>
    </div>
  );

  if (!landing) return <div style={{ padding: 60, textAlign: 'center', color: 'var(--ink-faint)' }}>Cargando...</div>;

  const { producto, diseno, eco_badges, secciones, shipping_note, variaciones } = landing;
  const precioBase = producto.precio + adicionalVariante;
  const tieneDescuento = producto.descuento_activo && producto.descuento_porcentaje > 0;
  const precioFinal = tieneDescuento ? Math.round(precioBase * (1 - producto.descuento_porcentaje / 100)) : precioBase;
  const sinStock = producto.stock <= 0;

  const abrirCheckout = () => {
    track('click_landing_cta', { producto_id: producto.id, producto_nombre: producto.nombre, valor: precioFinal, meta: { landing_slug: slug } });
    setModalOpen(true);
  };

  return (
    <div className="landing-page">
      <style>{`
        .landing-top { }
        @media (min-width: 900px) {
          .landing-top {
            display: grid; grid-template-columns: 1fr 1fr; gap: 48px;
            max-width: 1120px; margin: 0 auto; padding: 32px 24px 0; align-items: start;
          }
          .landing-hero-wrap { border-radius: var(--r-lg); overflow: hidden; }
          .landing-info-col { padding: 8px 0 0; }
          .landing-sections-wrap { max-width: 860px; margin: 0 auto; }
        }
      `}</style>

      <div className="landing-top">
        <div className="landing-hero-wrap">
          <HeroSection diseno={diseno} />
        </div>

        <div className="landing-info-col" style={{ padding: '24px 20px 0' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, color: 'var(--forest)', marginBottom: 8 }}>{producto.nombre}</h2>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 16 }}>
            {tieneDescuento && <span style={{ fontSize: 16, color: 'var(--ink-faint)', textDecoration: 'line-through' }}>{fmtCOP(precioBase)}</span>}
            <span style={{ fontSize: 28, fontWeight: 900, color: 'var(--forest)' }}>{fmtCOP(precioFinal)}</span>
            {tieneDescuento && <span style={{ background: 'var(--terra)', color: '#fff', fontSize: 12, fontWeight: 800, padding: '3px 10px', borderRadius: 20 }}>-{producto.descuento_porcentaje}%</span>}
          </div>
          {producto.descripcion && <p style={{ fontSize: 14, color: 'var(--ink-light)', lineHeight: 1.7, marginBottom: 20 }}>{producto.descripcion}</p>}

          <ProductVariantSelector agrupadas={variaciones.agrupadas} onChange={(sel, adicional) => { setVariacionesSel(sel); setAdicionalVariante(adicional); }} />

          {variaciones.agrupadas && Object.keys(variaciones.agrupadas).length === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--forest)' }}>Cantidad</span>
              <button onClick={() => setCantidad(c => Math.max(1, c - 1))} style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid rgba(28,58,30,0.2)', background: '#fff', cursor: 'pointer', fontWeight: 700 }}>-</button>
              <span style={{ fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{cantidad}</span>
              <button onClick={() => setCantidad(c => c + 1)} style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid rgba(28,58,30,0.2)', background: '#fff', cursor: 'pointer', fontWeight: 700 }}>+</button>
            </div>
          )}

          {sinStock && (
            <div style={{ background: 'rgba(196,99,42,0.1)', color: 'var(--terra)', fontWeight: 700, fontSize: 13, padding: '10px 14px', borderRadius: 'var(--r-md)', marginBottom: 16, textAlign: 'center' }}>
              Producto agotado por el momento
            </div>
          )}

        </div>
      </div>

      <div className="landing-sections-wrap">
        <EcoBadgesGrid titulo="Nuestro Compromiso" items={eco_badges} />
        <SectionDivider />

        {secciones.map((s, i) => (
          <React.Fragment key={i}>
            {renderSeccion(s, i)}
            {i < secciones.length - 1 && <SectionDivider />}
          </React.Fragment>
        ))}

        {shipping_note && (
          <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--ink-faint)', padding: '0 20px' }}>🚚 {shipping_note}</div>
        )}
      </div>

      {/* Espaciador para que el botón fijo de abajo nunca tape el último contenido */}
      <div style={{ height: 76 }} />

      <BuyStickyButton
        precio={producto.precio + adicionalVariante}
        precioDescuento={tieneDescuento ? precioFinal : null}
        buttonText={diseno.button_text}
        onClick={abrirCheckout}
        disabled={sinStock}
      />

      {modalOpen && (
        <OneClickCheckoutModal
          producto={producto}
          cantidad={cantidad}
          variaciones={variacionesSel}
          precioUnitario={precioFinal}
          landingSlug={slug}
          onClose={() => setModalOpen(false)}
          onSuccess={(resultado) => {
            setModalOpen(false);
            navigate('/pedido-ok', { state: { numero: resultado.numero, total: resultado.total, metodo_pago: 'wompi', transaccion_id: resultado.transaccion_id } });
          }}
        />
      )}
    </div>
  );
}
